/**
 * Available lattices.
 *
 * Packs are build artifacts, produced by tools/pack.mjs from a Resonant
 * Lattice SQLite file, and gitignored. Run the exporter before the dev server
 * or the picker will list entries that 404.
 *
 * All ship at 512 dims with embeddinggemma-300m, both chosen by measurement.
 * See docs/JOURNAL.md for the recall bench that decided it.
 *
 * Ship-packs are curated from purpose-built research corpora, never exported
 * from live personal profiles. Standing rule, agreed 2026-07-26.
 */

export type PackEntry = {
  key: string;
  /** Short label for the picker. */
  label: string;
  /** Full persona name. Shown on load: "Loading The Mind's Gardener...". */
  persona: string;
  url: string;
  factCount: number;
  /** Approximate. The manifest carries the truth. */
  approxBytes: number;
  note: string;
  passphrase?: string;
};

export const PACKS: PackEntry[] = [
  {
    key: "spark-kit",
    label: "Spark Expert",
    persona: "The Mind's Spark Expert",
    url: "/lattices/spark-kit-embeddinggemma.rlm",
    factCount: 4211,
    approxBytes: 4_400_297,
    note: "The NVIDIA DGX Spark, agent wrappers, Linux server administration, Tailscale, and local inference serving. 3612 carry an attested source quote.",
  },
  {
    key: "webdev",
    label: "Web Developer",
    persona: "The Mind's Web Developer",
    url: "/lattices/webdev-embeddinggemma.rlm",
    factCount: 9886,
    approxBytes: 10_690_000,
    note: "Browser APIs, JavaScript, WebGL and WebGPU, build tooling, and flight-control code. The largest lattice here, and the stress test for brute-force search. 1085 web_search and terminal notes the research agent left itself were curated out.",
  },
  {
    key: "printing",
    label: "Printer",
    persona: "The Mind's Printer",
    url: "/lattices/printing-embeddinggemma.rlm",
    factCount: 1376,
    approxBytes: 1_364_435,
    note: "3D printing: PETG, TPU, flexibles, Klipper, slicer settings and workshop practice.",
  },
  {
    key: "gardening",
    label: "Gardener",
    persona: "The Mind's Gardener",
    url: "/lattices/gardening-embeddinggemma.rlm",
    factCount: 729,
    approxBytes: 722_000,
    note: "Seeds, soil, plant disease and growing practice. Its safety rule is in the preamble: no invented pesticide or fertilizer rates.",
  },
  {
    key: "hermes-trismegistus",
    label: "Hermeticist",
    persona: "The Mind's Hermeticist",
    url: "/lattices/hermes-trismegistus-embeddinggemma.rlm",
    factCount: 597,
    approxBytes: 755_000,
    note: "Hermes Trismegistus, the Hermetic corpus, and its transmission. Every doctrinal claim is tied to a named text or period.",
  },
];

export const DEFAULT_PACK_KEY = "spark-kit";

export const packByKey = (key: string) => PACKS.find((p) => p.key === key);
